import { useSpv } from "@inrange/building-manager-api-client";
import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import { SpvPerformance } from "@inrange/building-manager-api-client/models-spv";
import { NoPaddedRow, SimpleButton } from "@inrange/theme-components";
import {
  formatCurrency,
  formatUnits,
} from "@inrange/theme-components/formatting";
import { Table } from "@inrange/theme-components/table";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

type SPV = Organisation["spvs"][0];

interface FunderSpvListProps {
  organisation: Organisation;
}

interface SpvRowProps {
  orgId: string;
  spv: SPV;
  userOrgId?: string;
}

interface SpvData {
  spv: SPV;
  revenue: number | undefined;
  costs: number | undefined;
  payment: number | undefined;
  closingCash: number | undefined;
  dscr: number | undefined;
}

interface SpvHeaderProps {
  handleColumnClick: (columnName: string) => void;
  sortColumn: string;
  sortAscending: boolean;
}

const ColumnTextStyle = styled.div`
  display: flex;
  justify-content: start;
  gap: 5px;
`;

const formatNumberOrDash = (
  value: number | undefined,
  decimals: number = 2
) => {
  if (value === undefined || value === null) return "-";
  return value.toFixed(decimals);
};

const formatUnitsOrDash = (
  value: number | undefined,
  unit: string,
  decimals: number = 3
) => {
  if (value === undefined || value === null) return "-";
  return formatUnits(value, unit, decimals);
};

const formatCurrencyOrDash = (
  value: number | undefined,
  currency: string,
  decimals: number = 0
) => {
  if (value === undefined || value === null) return "-";
  return formatCurrency(value, currency, decimals);
};

const getSortingIcon = (
  sortColumn: string,
  sortValue: string,
  sortAscending: boolean
) => {
  if (sortColumn !== sortValue) {
    return <div style={{ width: "16px", height: "16px" }}></div>;
  }
  return sortAscending ? (
    <span style={{ fontSize: "16px" }}>↑</span>
  ) : (
    <span style={{ fontSize: "16px" }}>↓</span>
  );
};

const SpvHeader: React.FC<SpvHeaderProps> = ({
  handleColumnClick,
  sortColumn,
  sortAscending,
}) => {
  const headerData = [
    { columnName: "name", label: "SPV" },
    { columnName: "generation", label: "Generation last quarter" },
    { columnName: "revenue", label: "Revenue last quarter" },
    { columnName: "costs", label: "Costs last quarter" },
    { columnName: "payment", label: "Payment made last quarter" },
    { columnName: "dscr", label: "DSCR last quarter" },
  ];

  return (
    <tr>
      {headerData.map((header) => (
        <th
          key={header.columnName}
          onClick={() => handleColumnClick(header.columnName)}
          data-testid={`table-header-${header.columnName}`}
        >
          <ColumnTextStyle>
            {header.label}
            {getSortingIcon(sortColumn, header.columnName, sortAscending)}
          </ColumnTextStyle>
        </th>
      ))}
      <th></th>
    </tr>
  );
};

const SpvRow: React.FC<SpvRowProps> = ({ orgId, spv, userOrgId }) => {
  const navigate = useNavigate();
  const { fetchSpv } = useSpv(orgId, spv.uuid, userOrgId);
  const spvPerformance: SpvPerformance | undefined =
    fetchSpv.data?.spvPerformance;

  const lastQuarter =
    spvPerformance?.quarters[spvPerformance.quarters.length - 1];
  const actuals = lastQuarter?.actual;

  const handleViewSpv = () => {
    navigate(`/org/${orgId}/spv/${spv.uuid}`);
  };

  return (
    <tr>
      <td>
        <div style={{ color: "#101828", fontWeight: "bold" }}>{spv.name}</div>
        {spvPerformance && (
          <div>
            {spvPerformance.sites.length} site
            {spvPerformance.sites.length !== 1 ? "s" : ""}
          </div>
        )}
      </td>
      <td>{formatUnitsOrDash(actuals?.generation || undefined, "kWh", 3)}</td>
      <td>
        {formatCurrencyOrDash(
          actuals?.revenue || undefined,
          spvPerformance?.currencyCode || "GBP",
          2
        )}
      </td>
      <td>
        {formatCurrencyOrDash(
          actuals?.costs || undefined,
          spvPerformance?.currencyCode || "GBP",
          2
        )}
      </td>
      <td>
        {formatCurrencyOrDash(
          actuals?.payment || undefined,
          spvPerformance?.currencyCode || "GBP",
          2
        )}
      </td>
      <td>{formatNumberOrDash(actuals?.dscr)}</td>
      <td>
        <SimpleButton label="View SPV" onClick={handleViewSpv} />
      </td>
    </tr>
  );
};

const sortByProperty = (
  spvData: SpvData[],
  sortAscending: boolean,
  propertyAccessor: (data: SpvData) => number | string | undefined
): void => {
  spvData.sort((a, b) => {
    const propertyA = propertyAccessor(a);
    const propertyB = propertyAccessor(b);

    if (propertyA === undefined && propertyB === undefined) return 0;
    if (propertyA === undefined) return 1;
    if (propertyB === undefined) return -1;

    if (typeof propertyA === "string" && typeof propertyB === "string") {
      return sortAscending
        ? propertyA.localeCompare(propertyB)
        : propertyB.localeCompare(propertyA);
    }

    const numA = propertyA as number;
    const numB = propertyB as number;
    return sortAscending ? numA - numB : numB - numA;
  });
};

const sortByColumn = (
  spvData: SpvData[],
  sortColumn: string,
  sortAscending: boolean
) => {
  const sortFunctions: Record<string, (data: SpvData[]) => SpvData[] | void> = {
    name: (data) => sortByProperty(data, sortAscending, (d) => d.spv.name),
    revenue: (data) => sortByProperty(data, sortAscending, (d) => d.revenue),
    costs: (data) => sortByProperty(data, sortAscending, (d) => d.costs),
    payment: (data) => sortByProperty(data, sortAscending, (d) => d.payment),
    closingCash: (data) =>
      sortByProperty(data, sortAscending, (d) => d.closingCash),
    dscr: (data) => sortByProperty(data, sortAscending, (d) => d.dscr),
  };

  const sortFunction = sortFunctions[sortColumn];
  if (sortFunction) {
    sortFunction(spvData);
  }
};

const sortAscendingForColumn = (column: string): boolean => {
  const defaultAscending: Record<string, boolean> = {
    name: true,
  };
  return !!defaultAscending[column];
};

const togglesortAscending = (
  setSortAscending: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setSortAscending((prevSortAscending) => !prevSortAscending);
};

const FunderSpvList: React.FC<FunderSpvListProps> = ({ organisation }) => {
  const spvs = useMemo(() => organisation.spvs || [], [organisation.spvs]);
  const [sortColumn, setSortColumn] = useState<string>("name");
  const [sortAscending, setSortAscending] = useState<boolean>(
    sortAscendingForColumn("name")
  );
  const [sortedSpvData, setSortedSpvData] = useState<SpvData[]>([]);

  useEffect(() => {
    const spvDataPromises = spvs.map(async (spv) => {
      const spvData: SpvData = {
        spv,
        revenue: undefined,
        costs: undefined,
        payment: undefined,
        closingCash: undefined,
        dscr: undefined,
      };
      return spvData;
    });

    Promise.all(spvDataPromises).then((data) => {
      sortByColumn(data, sortColumn, sortAscending);
      setSortedSpvData(data);
    });
  }, [spvs, sortColumn, sortAscending]);

  const handleColumnClick = (columnKey: string) => {
    if (sortColumn === columnKey) {
      togglesortAscending(setSortAscending);
      return;
    }
    setSortColumn(columnKey);
    setSortAscending(sortAscendingForColumn(columnKey));
  };

  const tableHead = (
    <SpvHeader
      handleColumnClick={handleColumnClick}
      sortColumn={sortColumn}
      sortAscending={sortAscending}
    />
  );

  const tableRows = sortedSpvData.map((spvData) => (
    <SpvRow
      key={spvData.spv.uuid}
      orgId={organisation.id}
      spv={spvData.spv}
      userOrgId={organisation.id}
    />
  ));

  return (
    <>
      <NoPaddedRow>
        <Table head={tableHead} rows={tableRows} id="funder-spv-list-table" />
      </NoPaddedRow>
    </>
  );
};

export default FunderSpvList;
